const STORAGE_KEY = "tenbagger.auth";
const PENDING_REFERRAL_KEY = "pending_referral_code";

const EXPIRY_SKEW_MS = 5 * 60 * 1000;

export interface StoredAuth {
	access_token: string;
	expires_at: string;
	user_id: string;
	wallet_address: string;
	email: string | null;
	referred_by_code: string | null;
	/** Server-authoritative new-account flag from `/v1/auth/email/verify` (may be absent). */
	is_new_account?: boolean;
}

let cachedRaw: string | null = null;
let cachedAuth: StoredAuth | null = null;

export function getStoredAuth(): StoredAuth | null {
	if (typeof window === "undefined") return null;
	const raw = window.localStorage.getItem(STORAGE_KEY);
	if (raw === cachedRaw) return cachedAuth;
	cachedRaw = raw;
	cachedAuth = parseAuth(raw);
	return cachedAuth;
}

function parseAuth(raw: string | null): StoredAuth | null {
	if (!raw) return null;
	try {
		const parsed = JSON.parse(raw) as Partial<StoredAuth>;
		if (
			typeof parsed.access_token === "string" &&
			typeof parsed.expires_at === "string" &&
			typeof parsed.user_id === "string" &&
			typeof parsed.wallet_address === "string"
		) {
			return {
				access_token: parsed.access_token,
				expires_at: parsed.expires_at,
				user_id: parsed.user_id,
				wallet_address: parsed.wallet_address,
				email: typeof parsed.email === "string" ? parsed.email : null,
				referred_by_code:
					typeof parsed.referred_by_code === "string"
						? parsed.referred_by_code
						: null,
				is_new_account:
					typeof parsed.is_new_account === "boolean"
						? parsed.is_new_account
						: undefined,
			};
		}
		return null;
	} catch {
		return null;
	}
}

export function setStoredAuth(auth: StoredAuth): void {
	if (typeof window === "undefined") return;
	window.localStorage.setItem(STORAGE_KEY, JSON.stringify(auth));
	window.dispatchEvent(new Event("tenbagger:auth-changed"));
}

export function clearStoredAuth(): void {
	if (typeof window === "undefined") return;
	window.localStorage.removeItem(STORAGE_KEY);
	window.dispatchEvent(new Event("tenbagger:auth-changed"));
}

export function isAuthValid(auth: StoredAuth | null): auth is StoredAuth {
	if (!auth) return false;
	const expiresMs = Date.parse(auth.expires_at);
	if (Number.isNaN(expiresMs)) return false;
	return expiresMs - EXPIRY_SKEW_MS > Date.now();
}

export function getPendingReferralCode(): string | null {
	if (typeof window === "undefined") return null;
	return window.localStorage.getItem(PENDING_REFERRAL_KEY);
}

export function setPendingReferralCode(code: string): void {
	if (typeof window === "undefined") return;
	window.localStorage.setItem(PENDING_REFERRAL_KEY, code);
}

export function clearPendingReferralCode(): void {
	if (typeof window === "undefined") return;
	window.localStorage.removeItem(PENDING_REFERRAL_KEY);
}
